/**
 * Radar Color Lookup Table
 * Pre-computes RGB → ramp index mapping for fast pixel processing.
 * Uses 5-bit quantized RGB (32×32×32 = 32KB) for memory efficiency.
 */

/**
 * Convert RGB to HSV.
 * Returns h in [0,360], s in [0,1], v in [0,1].
 */
export function rgbToHsv(r: number, g: number, b: number): { h: number; s: number; v: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;

  const v = max;
  const s = max === 0 ? 0 : d / max;

  let h = 0;
  if (d !== 0) {
    if (max === rn) {
      h = ((gn - bn) / d + (gn < bn ? 6 : 0)) * 60;
    } else if (max === gn) {
      h = ((bn - rn) / d + 2) * 60;
    } else {
      h = ((rn - gn) / d + 4) * 60;
    }
  }

  return { h, s, v };
}

/**
 * Map an NWS pixel's HSV to a ramp index.
 * Returns -1 if the pixel should be made transparent.
 */
export function hsvToRampIndex(h: number, s: number, v: number): number {
  if (s < 0.15) return -1;
  if (v < 0.12) return -1;

  if (h >= 160 && h <= 200) {
    if (s < 0.3) return 0;
    return 1;
  }
  if (h >= 140 && h < 160) return 2;
  if (h >= 80 && h < 140) {
    if (h >= 120) return 2;
    if (h >= 100) return 3;
    return 4;
  }
  if (h >= 55 && h < 80) {
    if (s < 0.55) return -1;
    if (h >= 70) return 5;
    return 6;
  }
  if (h >= 45 && h < 55) {
    if (s < 0.50) return -1;
    return 7;
  }
  if (h >= 20 && h < 45) {
    if (s < 0.45) return -1;
    if (h >= 35) return 8;
    return 9;
  }
  if (h < 20 || h >= 345) {
    if (v > 0.5) return 10;
    return 11;
  }
  if (h >= 260 && h < 345) {
    if (h >= 310) return 12;
    if (v > 0.5) return 12;
    return 13;
  }
  if (h > 200 && h < 260) {
    if (s < 0.25) return -1;
    return 0;
  }
  return -1;
}

/** LUT size: 32×32×32 = 32,768 entries */
export const LUT_SIZE = 32;
export const LUT_TOTAL = LUT_SIZE * LUT_SIZE * LUT_SIZE;

/**
 * Build the color lookup table.
 * Maps quantized RGB (5 bits per channel) to ramp index (-1 to 13).
 * Called once at app startup.
 */
export function buildColorLUT(): Int8Array {
  const lut = new Int8Array(LUT_TOTAL);

  for (let r = 0; r < LUT_SIZE; r++) {
    for (let g = 0; g < LUT_SIZE; g++) {
      for (let b = 0; b < LUT_SIZE; b++) {
        // Convert 5-bit back to 8-bit (multiply by 8, or shift left 3)
        const r8 = (r << 3) | 4; // Add 4 for center of quantization bucket
        const g8 = (g << 3) | 4;
        const b8 = (b << 3) | 4;

        const { h, s, v } = rgbToHsv(r8, g8, b8);
        const index = r * LUT_SIZE * LUT_SIZE + g * LUT_SIZE + b;
        lut[index] = hsvToRampIndex(h, s, v);
      }
    }
  }

  return lut;
}

/**
 * Look up ramp index for an RGB color using the LUT.
 * @param lut - The pre-built lookup table
 * @param r - Red component (0-255)
 * @param g - Green component (0-255)
 * @param b - Blue component (0-255)
 * @returns Ramp index (-1 for transparent, 0-13 for color)
 */
export function lookupRampIndex(lut: Int8Array, r: number, g: number, b: number): number {
  // Quantize to 5 bits (divide by 8, or shift right 3)
  const rq = r >> 3;
  const gq = g >> 3;
  const bq = b >> 3;
  return lut[rq * LUT_SIZE * LUT_SIZE + gq * LUT_SIZE + bq];
}
